// App download buttons and the two app-link popups (rendered by
// app/templates/app-email-modal.html). Every button does what its label says:
//
// - Download for Mac [data-download-for-mac] is always the real download.
//   On a desktop, Windows included, the click reports "Download for Mac
//   Clicked" to Meta and the link is followed. On a phone or tablet, which
//   can't run a .dmg, it reports "Download for Mac Tapped on Mobile" and
//   opens "Finish on your Mac." instead.
// - Not on a Mac? [data-app-link-dialog-open] is a <button>, never a link to
//   the download, so it can't be counted as one. It opens the email popup
//   that offers the Mac link or the Windows waitlist.
//
// Email submits report "App Link Requested" (never Lead, which belongs to
// the Fieldstone campaign); the Windows waitlist reports its own event.
//
// This file also holds the Klaviyo, Fathom, and platform helpers that the
// Fieldstone landers and the newsletter form import.

import { trackMetaEvent } from './meta-events';

// Klaviyo API revision — bump deliberately, never auto-upgrade.
const KLAVIYO_REVISION = '2026-04-15';

declare global {
  interface Window {
    fathom?: { trackEvent?: (name: string) => void };
  }
}

// Fathom loads deferred and is often ad-blocked; never assume it exists.
export function trackEvent(name: string): void {
  if (typeof window.fathom?.trackEvent === 'function') {
    window.fathom.trackEvent(name);
  }
}

// The inline script in layout.html's <head> owns the Mac decision, because the
// CTA label has to be correct before first paint. Read its verdict rather than
// repeating the detection, so there is exactly one implementation of it.
export function isMacDesktop(): boolean {
  return document.documentElement.classList.contains('is-mac');
}

// The one Klaviyo client-subscribe implementation — the modal and the
// Fieldstone landers (src/fieldstone-lander.ts) both go through here.
// Success is a 202 with an empty body — never parse JSON.
export async function subscribeToKlaviyo(options: {
  companyId: string;
  listId: string;
  email: string;
  customSource: string;
  properties?: Record<string, string>;
}): Promise<void> {
  const { companyId, listId, email, customSource, properties } = options;
  const response = await fetch(
    `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: customSource,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  ...(properties ? { properties } : {}),
                },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      }),
    }
  );
  if (!response.ok) throw new Error(`Klaviyo subscription failed: ${response.status}`);
}

// A phone or tablet can't open the .dmg, so Download for Mac sends the link by
// email there instead. Desktops of every kind download directly: a Windows
// visitor who clicks Download for Mac asked for exactly that.
export function isPhoneOrTablet(): boolean {
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData?.mobile) return true;
  // Android tablets don't say "Mobile", but they do say "Android".
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  // iPadOS claims to be a Mac. Touch points are the tell, the same check the
  // .is-mac script in layout.html's <head> uses.
  return /Mac/i.test(navigator.platform || '') && (navigator.maxTouchPoints || 0) > 1;
}

// Report what the browser actually says, so Klaviyo can tell a phone visitor
// who wants the Mac link from a Windows visitor.
export function requestPlatform(): string {
  if (isMacDesktop()) return 'mac';

  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  const raw = uaData?.platform || navigator.platform || '';

  if (/iPhone|iPod/i.test(raw)) return 'iphone';
  // An iPad reports "MacIntel"; it only reaches here because the touch check
  // already ruled it out as a Mac.
  if (/iPad/i.test(raw) || /Mac/i.test(raw)) return 'ipad';
  if (/Win/i.test(raw)) return 'windows';
  if (/Android/i.test(raw)) return 'android';
  if (/Linux|X11/i.test(raw)) return 'linux';
  return 'unknown';
}


type AppLinkChoice = 'mac' | 'windows';

// One app-link popup. The page renders two: "not-on-mac" (Mac link or Windows
// waitlist) and "finish-on-mac" (Mac link only, for a phone that tapped
// Download for Mac). Each <dialog> gets its own instance.
class AppLinkDialog {
  private readonly companyId: string;
  private readonly appListId: string;
  private readonly windowsListId: string;
  private readonly form: HTMLFormElement;
  private readonly input: HTMLInputElement;
  private readonly submitButtons: HTMLButtonElement[];
  private readonly formState: HTMLElement;
  private readonly errorInvalid: HTMLElement | null;
  private readonly errorNetwork: HTMLElement | null;
  private submitting = false;
  private savedScrollY = 0;

  constructor(
    private readonly name: string,
    private readonly dialog: HTMLDialogElement
  ) {
    const form = dialog.querySelector<HTMLFormElement>('form');
    const input = dialog.querySelector<HTMLInputElement>('input[type="email"]');
    const formState = dialog.querySelector<HTMLElement>('[data-app-link-form-state]');
    const submitButtons = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button[type="submit"]'));
    if (!form || !input || !formState || submitButtons.length === 0) {
      throw new Error(`[app-link] popup "${name}" is missing its form, input, submit button, or form state.`);
    }

    this.companyId = dialog.dataset.klaviyoCompanyId ?? '';
    this.appListId = dialog.dataset.klaviyoListId ?? '';
    this.windowsListId = dialog.dataset.windowsListId ?? '';
    this.form = form;
    this.input = input;
    this.submitButtons = submitButtons;
    this.formState = formState;
    this.errorInvalid = dialog.querySelector<HTMLElement>('[data-app-link-error-invalid]');
    this.errorNetwork = dialog.querySelector<HTMLElement>('[data-app-link-error-network]');

    this.bindEvents();
  }

  private bindEvents(): void {
    this.dialog
      .querySelector('[data-app-link-close]')
      ?.addEventListener('click', () => this.dialog.close());

    // Backdrop clicks land on the dialog element itself; clicks inside the
    // padded content box land on descendants.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    // ESC dispatches `cancel` then `close`, so one `close` listener restores
    // scroll for all three close paths (button, backdrop, ESC).
    this.dialog.addEventListener('close', () => {
      document.body.classList.remove('app-email-modal-open');
      document.body.style.top = '';
      window.scrollTo(0, this.savedScrollY);
    });

    // Two submit buttons share one email field; the one pressed decides where
    // the email goes. Enter in the field would silently pick the first (Mac
    // link) and send a Windows visitor a .dmg, so with two choices Enter does
    // nothing and the visitor picks a button. With one choice, Enter submits.
    if (this.submitButtons.length > 1) {
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.isComposing) event.preventDefault();
      });
    }

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      const submitter = (event as SubmitEvent).submitter;
      const button = submitter instanceof HTMLButtonElement ? submitter : this.submitButtons[0];
      void this.submit(button);
    });
  }

  open(): void {
    if (this.dialog.open) return;
    this.hideErrors();
    // A reopened popup starts at the form, so someone who asked for the Mac
    // link can come back for the Windows waitlist.
    this.formState.hidden = false;
    this.dialog
      .querySelectorAll<HTMLElement>('[data-app-link-success]')
      .forEach((state) => (state.hidden = true));

    // Body scroll lock: pin the body with an explicit top offset so the
    // visual scroll position is preserved (same pattern as in-use-gallery).
    this.savedScrollY = window.scrollY;
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.classList.add('app-email-modal-open');

    this.dialog.showModal();
    trackEvent(`app-${this.name}-open`);
  }

  private async submit(button: HTMLButtonElement): Promise<void> {
    if (this.submitting) return;
    this.hideErrors();

    const choice = button.dataset.appLinkChoice;
    if (choice !== 'mac' && choice !== 'windows') {
      throw new Error(`[app-link] submit button in "${this.name}" has no valid data-app-link-choice.`);
    }

    if (!this.input.checkValidity()) {
      if (this.errorInvalid) this.errorInvalid.hidden = false;
      this.input.focus();
      return;
    }

    this.submitting = true;
    this.submitButtons.forEach((b) => (b.disabled = true));
    button.classList.add('is-loading');

    try {
      const email = this.input.value.trim();
      if (choice === 'mac') {
        await this.requestMacLink(email);
      } else {
        await this.joinWindowsWaitlist(email);
      }
      this.showSuccess(choice);
    } catch (error) {
      console.error(`[app-link] "${this.name}" ${choice} signup failed.`, error);
      if (this.errorNetwork) this.errorNetwork.hidden = false;
    } finally {
      this.submitting = false;
      this.submitButtons.forEach((b) => (b.disabled = false));
      button.classList.remove('is-loading');
    }
  }

  // The app list's Klaviyo flow emails the Mac download link.
  private async requestMacLink(email: string): Promise<void> {
    await subscribeToKlaviyo({
      companyId: this.companyId,
      listId: this.appListId,
      email,
      customSource: 'App download modal',
      properties: { download_request_platform: requestPlatform(), app_link_popup: this.name },
    });
    trackMetaEvent('App Link Requested', { popup: this.name });
    trackEvent('app-email-capture');
  }

  private async joinWindowsWaitlist(email: string): Promise<void> {
    if (!this.windowsListId) {
      throw new Error(`[app-link] popup "${this.name}" offers Windows but has no data-windows-list-id.`);
    }
    await subscribeToKlaviyo({
      companyId: this.companyId,
      listId: this.windowsListId,
      email,
      customSource: 'App page Windows waitlist',
      properties: { download_request_platform: requestPlatform() },
    });
    trackMetaEvent('Windows Waitlist Joined');
    trackEvent('app-windows-waitlist');
  }

  private showSuccess(choice: AppLinkChoice): void {
    const success = this.dialog.querySelector<HTMLElement>(`[data-app-link-success="${choice}"]`);
    if (!success) {
      throw new Error(`[app-link] popup "${this.name}" has no "${choice}" success state.`);
    }
    this.formState.hidden = true;
    success.hidden = false;
    success.querySelector<HTMLElement>('[tabindex="-1"]')?.focus();
  }

  private hideErrors(): void {
    if (this.errorInvalid) this.errorInvalid.hidden = true;
    if (this.errorNetwork) this.errorNetwork.hidden = true;
  }
}

function initAppDownloadButtons(): void {
  // The popups are absent when KLAVIYO_APP_LIST_ID is unset; Download for Mac
  // then stays a plain download everywhere.
  const dialogs = new Map<string, AppLinkDialog>();
  document.querySelectorAll<HTMLDialogElement>('[data-app-link-dialog]').forEach((el) => {
    const name = el.dataset.appLinkDialog ?? '';
    dialogs.set(name, new AppLinkDialog(name, el));
  });

  // One delegated listener covers every button on the page.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const download = target.closest<HTMLElement>('[data-download-for-mac]');
    if (download) {
      const params = { placement: download.dataset.downloadForMac ?? '' };
      const finishOnMac = dialogs.get('finish-on-mac');
      // A phone tap downloads nothing, so it gets its own event. Download for
      // Mac Clicked stays a clean count of real downloads for the ad sets to
      // optimize on.
      if (finishOnMac && isPhoneOrTablet()) {
        trackMetaEvent('Download for Mac Tapped on Mobile', params);
        event.preventDefault();
        finishOnMac.open();
      } else {
        trackMetaEvent('Download for Mac Clicked', params);
      }
      return;
    }

    const opener = target.closest<HTMLElement>('[data-app-link-dialog-open]');
    if (opener) {
      const name = opener.dataset.appLinkDialogOpen ?? '';
      const dialog = dialogs.get(name);
      if (!dialog) throw new Error(`[app-link] no popup named "${name}" on this page.`);
      dialog.open();
      return;
    }

    // The Fieldstone Mac lander's desktop CTA still uses the old trigger: a
    // download link that opens the email popup off a Mac. It moves to the
    // two-button pattern in the landers' own round.
    if (target.closest('[data-app-email-modal]') && !isMacDesktop()) {
      const notOnMac = dialogs.get('not-on-mac');
      if (notOnMac) {
        event.preventDefault();
        notOnMac.open();
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAppDownloadButtons);
} else {
  initAppDownloadButtons();
}
